// „Фискални проблеми“ (Мониторинг): сметките, чийто фискален документ не е
// потвърден на хартия или е на хартия без запис във „Фактури“, и повторението
// на сторното. Изнесено от `bills.ts` (правилото за размера); типовете на
// документа от ФУ (`FiscalDocumentFields`) остават там — общи с `/close`.
import { api } from "./client";
import type {
  BillCardPayment,
  FiscalDocumentFields,
  FiscalDocumentRecordError,
  FiscalResult,
} from "./bills";

// Затворени сметки с неуспешна фискализация + причината от FiscalAPI.
// failed = принтерът отказа (бон НЕ е излизал); timeout = изходът е
// неизвестен; pending = чака се резултат / reconciler.
export interface FiscalIssue {
  bill_id: number;
  bill_name: string;
  closed_at: string | null;
  total_amount: string;
  // Бакшишът по сметката (низ, "0.00" без бакшиш) — бонът, който се
  // повтаря, е поръчаното + бакшиша. Optional срещу по-старо BE.
  tip_amount?: string;
  waiter_id: number | null;
  waiter_name: string | null;
  payment_method_id: number | null;
  payment_method_name: string | null;
  fiscal_status: "pending" | "timeout" | "failed";
  fiscal_error: string | null;
  attempts: number;
  last_attempt_at: string | null;
  request_id: string;
  // Неуредено картово теглене по сметката (пари взети, бон няма) — показва
  // се в списъка, за да не посегне никой към повторно теглене.
  card?: BillCardPayment | null;
  // Опитът е бил фискална фактура: повторението я печата пак — с държания
  // номер (`fiscal_invoice_number`), а без такъв (върнат, анулиран) — с нов.
  // Само на `issues`; optional срещу по-старо BE.
  fiscal_invoice?: boolean;
  fiscal_invoice_number?: string | null;
}

// Сторно ред: сметката ИМА касова бележка (receipt_number), но кредитното
// известие към нея не е потвърдено. Отделен списък с ОТДЕЛЕН retry път —
// `/storno/retry`, не `/close/retry` (слети, retry би издал втори бон).
export interface StornoIssue extends FiscalIssue {
  receipt_number: string | null;
}

// Фискална фактура / кредитно известие, които СА на хартия (бонът/сторното е
// успешно), но не са записани във „Фактури" (`invoice_record_error` върху
// fiscal_jobs реда). Без retry: повторението би издало втори документ и би
// изгорило още един номер от общата поредица — редът казва какво да се провери.
export interface FiscalInvoiceIssue {
  bill_id: number;
  // "storno" → кредитно известие; бонът ("receipt"/"card_receipt") → фактура.
  job_type: string;
  request_id: string;
  last_attempt_at: string | null;
  invoice_number: string | null;
  receipt_number: string | null;
  error: FiscalDocumentRecordError | null;
}

export interface FiscalIssuesResponse {
  issues: FiscalIssue[];
  storno_issues: StornoIssue[];
  // Optional срещу по-старо BE.
  invoice_issues?: FiscalInvoiceIssue[];
}

export const getFiscalIssues = async (
  days = 7,
): Promise<FiscalIssuesResponse> => {
  const response = await api.get<FiscalIssuesResponse>(
    `/bills/fiscal-issues`,
    { params: { days } },
  );
  return response.data;
};

// Повтаря сторно с неуспешен/неизвестен изход. Без тяло: BE преизползва
// записания payload на първия опит (при неизвестен изход — и същия
// request_id, така FiscalAPI дедупва и второ кредитно известие не излиза).
// Сторно на сметка с фискална фактура носи и `credit_note`/`credit_note_error`.
export interface RetryStornoResult
  extends Pick<FiscalDocumentFields, "credit_note" | "credit_note_error"> {
  bill_id: number;
  fiscal_result?: FiscalResult;
}

export const retryStorno = async (billId: number): Promise<RetryStornoResult> => {
  const response = await api.post<RetryStornoResult>(`/bills/${billId}/storno/retry`);
  return response.data;
};
